import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { articles, type ArticleData } from '@/mocks/articles';

interface ArticlePageProps {
  slug?: string;
}

function renderSection(section: ArticleData['sections'][0], idx: number) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10 first:mt-0">
          {section.text}
        </h2>
      );
    case 'subheading':
      return (
        <h3 key={idx} className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-3 mt-8">
          {section.text}
        </h3>
      );
    case 'paragraph':
      return (
        <p key={idx} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
          {section.text}
        </p>
      );
    case 'list':
      return (
        <ul key={idx} className="list-disc pl-5 space-y-2 text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
          {section.items?.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div key={idx} className="bg-background-100 rounded-xl p-5 md:p-6 mb-6 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-200">
                {section.rows?.[0]?.cells.map((cell, i) => (
                  <th key={i} className={`py-2 pr-4 ${i === 0 ? 'text-foreground-500 font-medium' : i === 1 ? 'text-accent-600 font-medium' : 'text-foreground-400 font-medium'}`}>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-foreground-700">
              {section.rows?.slice(1).map((row, ri) => (
                <tr key={ri} className="border-b border-background-200/70 last:border-0">
                  {row.cells.map((cell, ci) => (
                    <td key={ci} className={`py-2 pr-4 ${ci === 1 ? 'text-accent-700 font-medium' : ''}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'cta':
      return (
        <div key={idx} className="bg-accent-50 rounded-2xl p-6 md:p-8 border border-accent-100 mb-12 mt-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
            Ready to Start Exploring?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
            {section.text}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center px-6 py-3 bg-accent-600 text-white rounded-full font-semibold text-sm hover:bg-accent-700 transition-all duration-200 cursor-pointer"
            >
              Explore Hipobuy Spreadsheet
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center justify-center px-6 py-3 border border-accent-300 text-accent-700 rounded-full font-semibold text-sm hover:bg-accent-100 transition-all duration-200 cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage({ slug }: ArticlePageProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const article = slug ? articles[slug] : null;

  const resolvedTitle = article?.title || 'The Complete Guide to Hipobuy Spreadsheet';
  const resolvedDescription = article?.description || 'Everything you need to know about Hipobuy Spreadsheet.';
  const resolvedBadge = article?.badge || 'Pillar Guide';
  const resolvedImage = article?.imageUrl || 'https://readdy.ai/api/search-image?query=Clean%20organized%20workspace%20with%20laptop%20displaying%20product%20spreadsheet%20data%2C%20minimalist%20desk%20setup%20with%20warm%20natural%20lighting%2C%20coffee%20cup%20and%20notebook%2C%20modern%20productive%20aesthetic%2C%20soft%20neutral%20tones%2C%20editorial%20photography%20with%20airy%20composition&width=1720&height=740&seq=article-fallback&orientation=landscape';
  const resolvedDate = article?.dateModified || '2026-07-12';
  const resolvedRead = article?.readTime || '12 min read';
  const resolvedSections = article?.sections || [];
  const resolvedFaqs = article?.faqs || [];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: resolvedTitle,
    description: resolvedDescription,
    image: resolvedImage,
    dateModified: resolvedDate,
    author: { '@type': 'Organization', name: 'Hipobuy Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'Hipobuy Spreadsheet', url: siteUrl, logo: `${siteUrl}/favicon.jpeg` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/${slug || 'hipobuy-spreadsheet-guide'}/` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: resolvedTitle, item: `${siteUrl}/${slug || 'hipobuy-spreadsheet-guide'}/` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="w-full px-4 md:px-10 max-w-[860px] mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap">
            <Link to="/" className="hover:text-accent-600 transition-colors duration-150">Home</Link>
            <span>/</span>
            <span className="text-foreground-600">{resolvedBadge}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-accent-700 text-xs font-medium tracking-wider mb-4">
              {resolvedBadge}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              {resolvedTitle}
            </h1>
            <p className="mt-4 text-foreground-500 text-sm md:text-base leading-relaxed">
              {resolvedDescription}
            </p>
            <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
              <span>Updated July 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{resolvedRead}</span>
            </div>
          </header>

          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden bg-background-100 mb-12">
            <img
              src={resolvedImage}
              alt={resolvedTitle}
              title={resolvedTitle}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {resolvedSections.map((section, idx) => renderSection(section, idx))}

            {resolvedFaqs.length > 0 && (
              <section className="mb-12 mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {resolvedFaqs.map((faq) => (
                    <details key={faq.q} className="bg-background-100 rounded-xl group">
                      <summary className="px-5 py-4 cursor-pointer text-foreground-900 text-sm font-medium list-none flex items-center justify-between">
                        {faq.q}
                        <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                      </summary>
                      <p className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {!slug && (
              <section className="mb-12 bg-accent-50 rounded-2xl p-6 md:p-8 border border-accent-100">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  Ready to Start Exploring?
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
                  Now that you understand how Hipobuy Spreadsheet works, it is time to put that knowledge into action.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap inline-flex items-center justify-center px-6 py-3 bg-accent-600 text-white rounded-full font-semibold text-sm hover:bg-accent-700 transition-all duration-200 cursor-pointer"
                  >
                    Explore Hipobuy Spreadsheet
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                  <Link
                    to="/"
                    className="whitespace-nowrap inline-flex items-center justify-center px-6 py-3 border border-accent-300 text-accent-700 rounded-full font-semibold text-sm hover:bg-accent-100 transition-all duration-200 cursor-pointer"
                  >
                    Back to Home
                  </Link>
                </div>
              </section>
            )}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200">
            <p className="text-foreground-400 text-xs mb-4">
              Internal Links:
            </p>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="text-xs text-accent-600 hover:text-accent-700 underline">Home Page</Link>
              {slug && slug !== 'hipobuy-spreadsheet' && (
                <Link to="/hipobuy-spreadsheet/" className="text-xs text-accent-600 hover:text-accent-700 underline">Hipobuy Spreadsheet Guide</Link>
              )}
              {slug && !slug.startsWith('hipobuy-spreadsheet-comparison') && (
                <Link to="/hipobuy-spreadsheet-comparison/" className="text-xs text-accent-600 hover:text-accent-700 underline">Complete Comparison</Link>
              )}
              {slug && !slug.startsWith('hipobuy-spreadsheet-safety') && (
                <Link to="/hipobuy-spreadsheet-safety/" className="text-xs text-accent-600 hover:text-accent-700 underline">Safety Guide</Link>
              )}
              {slug && !slug.startsWith('hipobuy-spreadsheet-categories') && (
                <Link to="/hipobuy-spreadsheet-categories/" className="text-xs text-accent-600 hover:text-accent-700 underline">Categories Guide</Link>
              )}
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-foreground-700">Main Website</a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}